/* ============================================
   UTILIDADES DEL TEMA PARA JAVASCRIPT
   ============================================ */

/**
 * Obtiene el valor de una variable CSS
 * @param {string} varName - Nombre de la variable sin los guiones (ej: 'primary-500')
 * @returns {string} - Valor de la variable CSS
 */
export const getCSSVar = (varName) => {
  if (typeof window !== 'undefined') {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(`--${varName}`)
      .trim();
  }
  return '';
};

/**
 * Colores de macronutrientes
 */
export const macroColors = {
  kilocalories: {
    color: 'var(--macro-kilocalories)',
    light: 'var(--macro-kilocalories-light)',
    gradient: 'var(--macro-kilocalories-gradient)',
    getCSSColor: () => getCSSVar('macro-kilocalories'),
    getCSSLight: () => getCSSVar('macro-kilocalories-light'),
  },
  carbs: {
    color: 'var(--macro-carbs)',
    light: 'var(--macro-carbs-light)',
    gradient: 'var(--macro-carbs-gradient)',
    getCSSColor: () => getCSSVar('macro-carbs'),
    getCSSLight: () => getCSSVar('macro-carbs-light'),
  },
  protein: {
    color: 'var(--macro-protein)',
    light: 'var(--macro-protein-light)',
    gradient: 'var(--macro-protein-gradient)',
    getCSSColor: () => getCSSVar('macro-protein'),
    getCSSLight: () => getCSSVar('macro-protein-light'),
  },
  fat: {
    color: 'var(--macro-fat)',
    light: 'var(--macro-fat-light)',
    gradient: 'var(--macro-fat-gradient)',
    getCSSColor: () => getCSSVar('macro-fat'),
    getCSSLight: () => getCSSVar('macro-fat-light'),
  },
};

/**
 * Obtiene el color de un macronutriente
 * @param {string} macroType - Tipo de macronutriente (kilocalories, carbs, protein, fat)
 * @param {string} variant - Variante del color (color, light, gradient)
 * @returns {string} - Color CSS
 */
export const getMacroColor = (macroType, variant = 'color') => {
  return macroColors[macroType]?.[variant] || macroColors.kilocalories.color;
};

/**
 * Genera estilos para barras de progreso de macronutrientes
 * @param {string} macroType - Tipo de macronutriente
 * @param {number} percentage - Porcentaje de progreso (0-100)
 * @param {boolean} exceeded - Si se ha excedido el límite
 * @returns {Object} - Objeto de estilos
 */
export const getMacroProgressStyles = (macroType, percentage = 0, exceeded = false) => {
  const baseStyles = {
    width: `${Math.min(percentage, 100)}%`,
    transition: 'var(--transition-normal)',
  };

  if (exceeded) {
    baseStyles.background = 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)';
  } else {
    baseStyles.background = macroColors[macroType]?.gradient || macroColors.kilocalories.gradient;
  }

  return baseStyles;
};

/**
 * Genera clases CSS para componentes de dashboard
 * @param {Object} options - Opciones de configuración
 * @returns {string} - Clases CSS concatenadas
 */
export const getDashboardClasses = (options = {}) => {
  const {
    isCard = false,
    isPrimary = false,
    isOnboarding = false,
    additionalClasses = []
  } = options;

  const classes = [];

  if (isCard) classes.push('dashboard-card');
  if (isPrimary) classes.push('dashboard-action-btn primary');
  else if (options.isActionBtn) classes.push('dashboard-action-btn');
  if (isOnboarding) classes.push('onboarding-card');

  return [...classes, ...additionalClasses].join(' ').trim();
};

/**
 * Nombres en español de los macronutrientes
 */
export const macroNames = {
  kilocalories: 'Kilocalorías',
  carbs: 'Carbohidratos',
  protein: 'Proteínas',
  fat: 'Grasas',
};

/**
 * Nombres cortos de los macronutrientes
 */
export const macroShortNames = {
  kilocalories: 'Kcal',
  carbs: 'Carbs',
  protein: 'Prot',
  fat: 'Gras',
};

/**
 * Iniciales de los macronutrientes (versión muy corta)
 */
export const macroInitials = {
  kilocalories: 'K',
  carbs: 'C',
  protein: 'P',
  fat: 'G',
};

/**
 * Unidades de los macronutrientes
 */
export const macroUnits = {
  kilocalories: 'kcal',
  carbs: 'g',
  protein: 'g',
  fat: 'g',
};

/**
 * Breakpoints del sistema de diseño
 */
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Utilidad para media queries responsive
 * @param {string} size - Tamaño del breakpoint
 * @returns {string} - Media query CSS
 */
export const mediaQuery = (size) => {
  return `@media (min-width: ${breakpoints[size]}px)`;
};

/**
 * Espaciado del sistema de diseño en píxeles
 */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
  '5xl': 48,
};

/**
 * Radio de bordes del sistema de diseño
 */
export const borderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  full: 9999,
};

/**
 * Duración de transiciones
 */
export const transitions = {
  fast: '0.15s',
  normal: '0.3s',
  slow: '0.5s',
};

/**
 * Z-index del sistema de diseño
 */
export const zIndex = {
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
  toast: 80,
};

/**
 * Tamaños de fuente en rem
 */
export const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
};

/**
 * Pesos de fuente
 */
export const fontWeight = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

/**
 * Utilidad para generar estilos inline con variables CSS
 * @param {Object} styles - Objeto con estilos
 * @returns {Object} - Objeto de estilos válido para React
 */
export const generateStyles = (styles) => {
  const processedStyles = {};

  Object.keys(styles).forEach(key => {
    let value = styles[key];

    // Si el valor contiene var(), lo mantenemos como string
    if (typeof value === 'string' && value.includes('var(')) {
      processedStyles[key] = value;
    } else {
      processedStyles[key] = value;
    }
  });

  return processedStyles;
};

/**
 * Hook personalizado para detectar el tema del sistema (claro/oscuro)
 * @returns {string} - 'light' o 'dark'
 */
export const useSystemTheme = () => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

/**
 * Tema personalizado para Material-UI que respeta las variables CSS
 */
export const createMuiTheme = () => {
  return {
    palette: {
      primary: {
        main: getCSSVar('primary-500'),
        light: getCSSVar('primary-300'),
        dark: getCSSVar('primary-700'),
      },
      secondary: {
        main: getCSSVar('secondary-500'),
        light: getCSSVar('secondary-300'),
        dark: getCSSVar('secondary-700'),
      },
      success: {
        main: getCSSVar('success-500'),
        light: getCSSVar('success-300'),
        dark: getCSSVar('success-700'),
      },
      warning: {
        main: getCSSVar('warning-500'),
        light: getCSSVar('warning-300'),
        dark: getCSSVar('warning-700'),
      },
      error: {
        main: getCSSVar('error-500'),
        light: getCSSVar('error-300'),
        dark: getCSSVar('error-700'),
      },
      background: {
        default: getCSSVar('background-secondary'),
        paper: getCSSVar('background-primary'),
      },
      text: {
        primary: getCSSVar('text-primary'),
        secondary: getCSSVar('text-secondary'),
      },
    },
    typography: {
      fontFamily: getCSSVar('font-family-sans'),
      fontSize: 16,
    },
    shape: {
      borderRadius: parseInt(getCSSVar('border-radius-md')),
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: getCSSVar('font-weight-semibold'),
            borderRadius: getCSSVar('border-radius-md'),
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: getCSSVar('border-radius-md'),
            },
          },
        },
      },
    },
  };
};

/**
 * Colores de macronutrientes para uso directo en Material-UI
 * Sincronizado con las variables CSS globales
 */
export const materialUIMacroColors = {
  kilocalories: {
    main: '#f093fb',
    light: '#fdf2f8',
    cssVar: 'var(--macro-kilocalories-color)',
    cssGradient: 'var(--macro-kilocalories-gradient)',
  },
  carbs: {
    main: '#fbbf24',
    light: '#fffbeb',
    cssVar: 'var(--macro-carbs-color)',
    cssGradient: 'var(--macro-carbs-gradient)',
  },
  protein: {
    main: '#4ecdc4',
    light: '#ecfdf5',
    cssVar: 'var(--macro-protein-color)',
    cssGradient: 'var(--macro-protein-gradient)',
  },
  fat: {
    main: '#667eea',
    light: '#eef2ff',
    cssVar: 'var(--macro-fat-color)',
    cssGradient: 'var(--macro-fat-gradient)',
  },
};

/**
 * Obtiene el color de un macronutriente para Material-UI
 * @param {string} macroType - Tipo de macronutriente (kilocalories, carbs, protein, fat)
 * @param {string} variant - Variante del color (main, light)
 * @returns {string} - Color para Material-UI
 */
export const getMaterialUIMacroColor = (macroType, variant = 'main') => {
  return materialUIMacroColors[macroType]?.[variant] || materialUIMacroColors.kilocalories.main;
};

/**
 * Obtiene la variable CSS de un macronutriente
 * @param {string} macroType - Tipo de macronutriente (kilocalories, carbs, protein, fat)
 * @param {string} variant - Variante del color (cssVar, cssGradient)
 * @returns {string} - Variable CSS
 */
export const getMacroCSSVar = (macroType, variant = 'cssVar') => {
  return materialUIMacroColors[macroType]?.[variant] || materialUIMacroColors.kilocalories.cssVar;
};

export default {
  macroColors,
  getMacroColor,
  macroNames,
  macroShortNames,
  macroUnits,
  breakpoints,
  mediaQuery,
  spacing,
  borderRadius,
  transitions,
  zIndex,
  fontSize,
  fontWeight,
  generateStyles,
  useSystemTheme,
  createMuiTheme,
  getCSSVar,
  materialUIMacroColors,
  getMaterialUIMacroColor,
  getMacroCSSVar,
}; 